'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { FC, memo, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useFlashSaleTracking } from '../hooks/useFlashSaleTracking';
import CountDownTimer from './CountDownTimer';

// 曝光检测组件
const ExposureDetector: FC<{
  item: any;
  index: number;
  onExposure: (item: any, index: number) => void;
}> = memo(({ item, index, onExposure }) => {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true, // 使用 triggerOnce: true，这是正确的做法
  });

  useEffect(() => {
    if (inView && item.id) {
      onExposure(item, index);
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
});

ExposureDetector.displayName = 'ExposureDetector';

interface FlashSaleAreaProps {
  listData: any[];
  flashSaleData: {
    title?: string;
    description?: string;
    left_image?: string;
    right_image?: string;
    sale_image?: string;
    price_word?: string;
    time_word?: string;
  };
  remainingSeconds: number;
  isCountdownVisible?: boolean; // 控制倒计时显示/隐藏
  onCountdownEnd?: () => void; // 倒计时结束回调
}

export default function FlashSaleAreaPc({
  listData,
  flashSaleData,
  remainingSeconds,
  isCountdownVisible = true,
  onCountdownEnd,
}: FlashSaleAreaProps) {
  const { navigate } = useAppNavigation();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const t = useTranslations('product');
  const swiperRef = useRef<SwiperType | null>(null);

  // 使用埋点追踪hook
  const { trackFlashSaleProductClick, trackFlashSaleProductExp } = useFlashSaleTracking();

  // 控制箭头显示状态
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // 获取商品图片
  const getProductImage = (item: any) => {
    return item?.image?.product?.[0] || '';
  };

  // 计算商品价格信息
  const getProductPriceInfo = (item: any) => {
    return getPriceInfo(userInfo, item, currency);
  };

  // 处理商品点击
  const handleProductClick = (item: any, index: number) => {
    trackFlashSaleProductClick(item, index);
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  // 商品卡片组件
  const ProductSlide = ({ item, index }: { item: any; index: number }) => {
    const priceData = getProductPriceInfo(item);
    const isSoldOut = item.stock <= 0;

    return (
      <div className="w-full h-full relative">
        <a
          href={`/goods-detail/${item.shop_sku}`}
          className="flex flex-col items-center justify-center w-full h-full relative pt-[20px] lg:pt-[30px] xl:pt-[50px] 2xl:pt-[70px] pb-[12px] lg:pb-[16px] xl:pb-[22px] 2xl:pb-[32px] box-border"
          onClick={(e) => {
            e.preventDefault();
            handleProductClick(item, index);
          }}
        >
          {/* 商品图片 */}
          <Image
            src={getProductImage(item)}
            alt={item.name}
            width={275}
            height={138}
            className="w-[59%] aspect-[2/1]"
          />

          {/* 商品名称 */}
          <span className="text-[18px] lg:text-[20px] xl:text-[22px] 2xl:text-[24px] font-bb text-[#282828] mt-[20px] lg:mt-[24px] xl:mt-[28px] 2xl:mt-[30px] text-center">
            {item.short_name}
          </span>

          {/* 价格区域 */}
          <div className="mt-[1px] text-center">
            {/* 原价（如果有折扣） */}
            <span className="text-[14px] lg:text-[15px] xl:text-[16px] text-[#A9A9A9] line-through notranslate">
              {formatPriceNumberWithSymbol(priceData.price, currency)}
            </span>

            {/* 现价盒子 */}
            <div className="flex items-center justify-between h-[44px] lg:h-[50px] xl:h-[54px] 2xl:h-[56px] min-w-[200px] lg:min-w-[230px] xl:min-w-[250px] 2xl:min-w-[268px] rounded-[22px] lg:rounded-[25px] xl:rounded-[27px] 2xl:rounded-[28px] overflow-hidden bg-[#ACD152] mt-[10px] lg:mt-[12px] xl:mt-[14px] relative">
              <p className="flex-1 text-[18px] lg:text-[22px] xl:text-[24px] 2xl:text-[26px] font-bb text-white pl-[8px] lg:pl-[14px] xl:pl-[20px] 2xl:pl-[28px] notranslate">
                {formatPriceNumberWithSymbol(priceData.sell_price, currency)}
              </p>
              {flashSaleData.sale_image && (
                <img
                  src={flashSaleData.sale_image}
                  alt=""
                  className="w-[26px] lg:w-[30px] xl:w-[33px] 2xl:w-[35px] h-[44px] lg:h-[50px] xl:h-[54px] 2xl:h-[56px]"
                />
              )}
              {flashSaleData.price_word && (
                <p className="flex items-center justify-center text-[18px] lg:text-[22px] xl:text-[24px] 2xl:text-[26px] font-bb bg-[#282828] h-full min-w-[64px] lg:min-w-[74px] xl:min-w-[82px] 2xl:min-w-[87px] text-[#FEEBE0] pr-[6px] lg:pr-[12px] xl:pr-[16px] 2xl:pr-[20px] box-border">
                  {flashSaleData.price_word}
                </p>
              )}
            </div>
          </div>

          {/* 售罄遮罩 */}
          {isSoldOut && (
            <div className="absolute inset-0 bg-white/40 rounded-[10px] lg:rounded-[12px] cursor-pointer">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-[23%] aspect-square rounded-full bg-black/60 text-center">
                <div
                  className="text-[14px] lg:text-[16px] xl:text-[17px] text-white whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
                />
              </span>
            </div>
          )}
        </a>
      </div>
    );
  };

  if (!listData || listData.length === 0) {
    return null;
  }

  return (
    <div className="mx-[80px] lg:mx-[120px] xl:mx-[180px] 2xl:mx-[220px] mb-[40px] lg:mb-[50px] xl:mb-[60px] 2xl:mb-[76px] h-auto rounded-[20px] relative pb-[16px] lg:pb-[20px] xl:pb-[23px] bg-[url(https://s3.zeelool.com/admin/shop/home/487f8552bfe14644a54be3b7f51703ac.png)] mt-[38px]">
      {/* 左侧装饰图片 */}
      {flashSaleData.left_image && (
        <img
          src={flashSaleData.left_image}
          alt=""
          className="w-[140px] lg:w-[180px] xl:w-[200px] 2xl:w-[215px] h-auto absolute top-0 left-0"
        />
      )}

      {/* 右侧装饰图片 */}
      {flashSaleData.right_image && (
        <img
          src={flashSaleData.right_image}
          alt=""
          className="w-[140px] lg:w-[180px] xl:w-[200px] 2xl:w-[215px] h-auto absolute top-0 right-0"
        />
      )}

      {/* 顶部区域 */}
      <div className="flex justify-between items-center px-[18px] lg:px-[24px] xl:px-[28px] 2xl:px-[32px] py-[12px] lg:py-[18px] 2xl:py-[24px] box-border z-10 relative">
        {/* 左侧：标题和描述 */}
        <div className="flex items-center text-[#282828]">
          {flashSaleData.title && (
            <h1 className="text-[28px] lg:text-[34px] xl:text-[38px] 2xl:text-[40px] font-bb mr-[8px] lg:mr-[10px]">
              {flashSaleData.title}
            </h1>
          )}
          {flashSaleData.description && (
            <h3 className="text-[14px] lg:text-[15px] xl:text-[16px]">
              {flashSaleData.description}
            </h3>
          )}
        </div>

        {/* 右侧：倒计时 */}
        <div className="flash-sale-countdown">
          <CountDownTimer
            remainingSeconds={remainingSeconds}
            flashSaleData={flashSaleData}
            isVisible={isCountdownVisible}
            onCountdownEnd={onCountdownEnd}
          />
        </div>
      </div>

      {/* 底部轮播区域 */}
      <div className="relative">
        <Swiper
          modules={[Navigation]}
          slidesPerView={3.0004}
          slidesPerGroup={1}
          spaceBetween={12}
          navigation={{
            prevEl: '.flash-sale-swiper-button-prev',
            nextEl: '.flash-sale-swiper-button-next',
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            // 初始化时更新状态
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper) => {
            // 滑动时更新箭头显示状态
            setIsBeginning(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          style={{
            padding: '0 32px',
          }}
        >
          {listData.map((item, index) => (
            <SwiperSlide
              key={item.id}
              className="h-auto rounded-[10px] lg:rounded-[12px] bg-white"
              style={{ marginRight: 0 }}
            >
              <ProductSlide item={item} index={index} />
              {/* 曝光检测组件 */}
              <ExposureDetector item={item} index={index} onExposure={trackFlashSaleProductExp} />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 左箭头 - 在开始位置时隐藏 */}
        {listData.length > 3 && (
          <div
            className={`flash-sale-swiper-button-prev w-[28px] lg:w-[30px] xl:w-[32px] h-[28px] lg:h-[30px] xl:h-[32px] bg-black/40 rounded-full flex items-center justify-center text-white text-[14px] lg:text-[15px] xl:text-[16px] absolute top-1/2 -translate-y-1/2 left-[30px] lg:left-[35px] xl:left-[40px] z-10 cursor-pointer ${isBeginning ? 'hidden' : ''}`}
          >
            <LeftArrowOutlined className={`text-[16px] relative right-[1px] text-white`} />
          </div>
        )}

        {/* 右箭头 - 在结束位置时隐藏 */}
        {listData.length > 3 && (
          <div
            className={`flash-sale-swiper-button-next w-[28px] lg:w-[30px] xl:w-[32px] h-[28px] lg:h-[30px] xl:h-[32px] bg-black/40 rounded-full flex items-center justify-center text-white text-[14px] lg:text-[15px] xl:text-[16px] absolute top-1/2 -translate-y-1/2 right-[30px] lg:right-[35px] xl:right-[40px] z-10 cursor-pointer ${isEnd ? 'hidden' : ''}`}
          >
            <RightArrowOutlined className={`text-[16px] relative text-white`} />
          </div>
        )}
      </div>
    </div>
  );
}
